/**
 * ENG-5687: merchant-configurable order for the side navigation and the Orders module tabs.
 *
 * The merchant's order is saved on the store as two JSON lists of item keys,
 * `store_data.nav_order` and `store_data.orders_tab_order`, through the ordinary store PATCH
 * (the same route `occasion_order` takes). An empty list means "never reordered" and every
 * client keeps its built-in order.
 *
 * The lists only SORT. Which items exist is still decided by modules and permissions at each
 * render site, exactly as before; a key the merchant dragged somewhere that this user cannot
 * see simply does not render. Keys a client does not know are ignored, and items missing from
 * a saved list keep their built-in order after the listed ones, so a module added later never
 * disappears.
 *
 * The native business app reads the same two lists and the same keys (its side nav is a
 * subset of this one; its tab builder uses the Orders tab keys below).
 *
 * The whole feature sits behind the `configurable_nav_order` store feature flag: off = today's
 * behaviour, pickers hidden, saved lists ignored.
 */

import { environment } from '../../environments/environment';

export const UI_ORDER_FLAG = 'configurable_nav_order';

export interface UiOrderItem {
  /** Stable key saved in the list. Shared with the native app — never rename. */
  key: string;
  label: string;
  /** Material icon, for the picker rows. */
  icon: string;
  /**
   * Store module that has to be enabled for the item to exist at all. The picker only lists
   * items the store can show; the render sites keep their own (module AND permission) gates.
   */
  module?: string;
  /** Extra store-level gate for the few items that are not plain module checks. */
  visible?: (store: any) => boolean;
  /** Always rendered first, whatever the merchant saved; the picker shows it locked. */
  pinned?: boolean;
}

export interface NavOrderItem extends UiOrderItem {
  /** Where the link goes — what the landing page opens when this item is first. */
  route: string;
  /** The `ACCESS` permission the link is gated on, if any (side-nav.component.html). */
  access?: string;
}

/**
 * Side-nav items, in the built-in order of side-nav.component.html. The sidebar is one list
 * to the merchant: the app links and the support links under them read as a single rail
 * (no divider), so a custom order spans both and the picker shows one list.
 */
export const NAV_ITEMS: NavOrderItem[] = [
  { key: 'home', label: 'Home', icon: 'store_mall_directory', route: '/home/info', access: 'HOME' },
  { key: 'orders', label: 'Orders', icon: 'shopping_bag', route: '/home/orders', module: 'DIGITAL_ORDERING', access: 'ORDERS' },
  {
    key: 'dispatch', label: 'Dispatch', icon: 'delivery_dining', route: '/dispatch', access: 'ORDERS',
    visible: store => !!store?.has_order_occasion_delivery && !!store?.feature_flag?.['delivery_v2']
  },
  { key: 'reporting', label: 'Reporting', icon: 'receipt', route: '/home/reporting', module: 'REPORTING_MODULE', access: 'REPORTING_TAB' },
  { key: 'menu', label: 'Menu', icon: 'local_dining', route: '/home/menu', module: 'MENU_MODULE', access: 'MENU' },
  { key: 'menu_photos', label: 'Menu Photos', icon: 'photo_camera', route: '/home/menu-photos', module: 'MENU_MODULE', access: 'MENU' },
  { key: 'invoices', label: 'Invoices', icon: 'receipt_long', route: '/home/invoice', module: 'INVOICE_MODULE', access: 'INVOICING_TAB' },
  { key: 'messages', label: 'Messages', icon: 'forum', route: '/home/messages', module: 'BUSINESS_MESSAGING', access: 'MESSAGES' },
  { key: 'gift_cards', label: 'Gift Cards', icon: 'card_giftcard', route: '/home/offers', module: 'DIGITAL_GIFT_CARDS', access: 'GIFT_CARDS' },
  { key: 'offers', label: 'Offers', icon: 'local_offer', route: '/home/offers-v2', module: 'OFFERS', access: 'OFFERS' },
  { key: 'campaign', label: 'Campaign', icon: 'announcement', route: '/home/campaign', module: 'SMS_MARKETING', access: 'CAMPAIGNS' },
  { key: 'attendant', label: 'Attendant', icon: 'phone_in_talk', route: '/home/attendant', module: 'ATTENDANT', access: 'ATTENDANT' },
  { key: 'voice_ordering', label: 'Voice Ordering', icon: 'record_voice_over', route: '/home/voice-ordering', module: 'VOICE_ORDERING', access: 'VOICE_ORDERING' },
  { key: 'digital_display', label: 'Digital Display', icon: 'airplay', route: '/home/digital-display', module: 'DIGITAL_DISPLAY_MODULE', access: 'DIGITAL_DISPLAY' },
  {
    key: 'devices', label: 'Devices', icon: 'devices', route: '/home/devices', access: 'DEVICES',
    visible: store => !!store?.feature_flag?.['device_fleet']
  },
  { key: 'loyalty', label: 'Loyalty', icon: 'sentiment_satisfied_alt', route: '/home/loyalty', module: 'LOYALTY_MODULE', access: 'LOYALTY' },
  { key: 'referral', label: 'Referral', icon: 'group_add', route: '/home/referral', module: 'REFERRAL_MODULE', access: 'REFERRAL' },
  { key: 'skills', label: 'Skills', icon: 'adb', route: '/home/skill', module: 'SKILLS_MODULE', access: 'SKILLS' },
  { key: 'feedback', label: 'Feedback', icon: 'feedback', route: '/home/feedback', module: 'FEEDBACK_MODULE', access: 'FEEDBACK' },
  { key: 'floor_plan', label: 'Floor Plan', icon: 'table_restaurant', route: '/home/floor-plan', module: 'FLOOR_PLAN_MODULE', access: 'FLOOR_PLAN' },
  { key: 'reservations', label: 'Reservations', icon: 'event_available', route: '/home/reservations', module: 'RESERVATIONS_MODULE', access: 'RESERVATIONS' },
  { key: 'influencers', label: 'Influencers', icon: 'campaign', route: '/home/influencers', module: 'INFLUENCER_MODULE', access: 'INFLUENCERS' },
  { key: 'kiosk', label: 'Kiosk', icon: 'tablet_android', route: '/home/kiosk', module: 'KIOSK_MODULE', access: 'KIOSK' },
  { key: 'third_party', label: 'Third Party', icon: 'storefront', route: '/home/kitchenhub', module: 'KITCHENHUB_MODULE', access: 'KITCHENHUB' },
  {
    key: 'google_business', label: 'Google Business', icon: 'business', route: '/home/google-business',
    visible: store => !!store?.feature_flag?.['google_business_profile']
  },

  { key: 'account', label: 'Account', icon: 'settings', route: '/home/account', module: 'ACCOUNT_MODULE', access: 'ACCOUNT' },
  { key: 'manage_users', label: 'Manage Users', icon: 'person_add_alt_1', route: '/home/users', module: 'MANAGE_USERS_MODULE', access: 'MANAGE_USERS' },
  {
    key: 'team_scheduling', label: 'Team Scheduling', icon: 'groups', route: '/home/team-member-scheduling',
    module: 'MANAGE_USERS_MODULE', access: 'MANAGE_USERS', visible: schedulingAllowedForStore
  },
  {
    key: 'my_schedule', label: 'My Schedule', icon: 'calendar_today', route: '/home/team-member-scheduling/my-schedule',
    module: 'MANAGE_USERS_MODULE', access: 'SCHEDULING', visible: schedulingAllowedForStore
  },
  { key: 'time_clock', label: 'Time Clock', icon: 'alarm_on', route: '/home/time-clock', module: 'TIME_CLOCK_MODULE', access: 'TIME_CLOCK' },
  { key: 'pos_settings', label: 'POS Settings', icon: 'tune', route: '/home/pos-settings', module: 'POS_SETTINGS_MODULE', access: 'POS_SETTINGS' },
  { key: 'settlements', label: 'Settlements', icon: 'summarize', route: '/home/settlements', module: 'SETTLEMENTS_MODULE', access: 'SETTLEMENTS' },
  { key: 'configuration', label: 'Configuration', icon: 'settings_applications', route: '/home/configuration', access: 'HOME' },
  { key: 'support', label: 'Support', icon: 'help', route: '/home/support', module: 'SUPPORT_MODULE', access: 'SUPPORT' },
];

/** Beta gate copied from side-nav.component.ts: on production, scheduling is only on for the demo store. */
function schedulingAllowedForStore(store: any): boolean {
  const PROD_API_HOSTS = ['backend.text-order.com', 'production.labrador.ai'];
  const apiUrl = (environment.apiUrl || '').toLowerCase();
  if (!PROD_API_HOSTS.some(h => apiUrl.includes(h))) { return true; }
  return (store?.store_name || '').toUpperCase().includes('PRODUCTION DEMO');
}

/** The default landing page, and what every route falls back to. */
export const HOME_ROUTE = '/home/info';

/**
 * The permission object that was current when a landing began (sign-in or store switch). On
 * a store switch the permission subject still holds the previous store's permissions until
 * the new request lands, and after a sign-out without a reload it holds the previous user's,
 * so the landing page must not decide on that object. In memory only: after a reload the
 * subject starts at null and any non-null emission is fresh.
 */
let stalePermission: any = null;

export function markNavLanding(currentPermission: any = null): void {
  stalePermission = currentPermission;
}

/** True for a permission emission the landing page may trust. */
export function isFreshPermission(permission: any): boolean {
  return !!permission && permission !== stalePermission;
}

/**
 * Where the app should open for this user: the first item of the merchant's side-nav order
 * that this user can see, or null when the store has no custom order (today's Home).
 *
 * "My Schedule" is only shown to a user WITHOUT Manage Users, mirroring the template, so the
 * two scheduling links never both count.
 */
export function firstNavRoute(store: any, permission: any): string | null {
  const saved = savedNavOrder(store);
  if (!saved.length || !permission?.hasPermission) { return null; }
  const can = (key: string) => !!permission.hasPermission('ACCESS', key);
  const visible = itemsForStore(NAV_ITEMS, store).filter(item => {
    if (item.access && !can(item.access)) { return false; }
    if (item.key === 'my_schedule' && can('MANAGE_USERS')) { return false; }
    return true;
  });
  const first = applyUiOrder(visible, saved, item => item.key)[0];
  return first ? first.route : null;
}

export interface OrdersTabItem extends UiOrderItem {
  /** The legacy mat-tab label (orders.component.ts DESIRED_ORDER). */
  tabLabel: string;
  // POS is pinned (UiOrderItem.pinned): the legacy tab strip treats index 0 as the POS tab in
  // a dozen places (onTabChange parks/restores the order hash on it, and Edit Order /
  // Checkout / caller-ID all jump to tab 0), so moving it would send those flows astray.
  /** The new-layout sidebar panel id (onSidebarNav); null is the POS view itself. */
  panel: 'open' | 'closed' | 'future' | 'deliveries' | 'reporting' | 'cash' | null;
}

/** Orders module tabs, in the built-in order (DESIRED_ORDER). */
export const ORDERS_TAB_ITEMS: OrdersTabItem[] = [
  { key: 'pos', label: 'POS', icon: 'storefront', tabLabel: 'POS', panel: null, module: 'POS', pinned: true },
  { key: 'closed_orders', label: 'Closed Orders', icon: 'check_circle', tabLabel: 'CLOSED ORDERS', panel: 'closed' },
  { key: 'open_orders', label: 'Open Orders', icon: 'assignment', tabLabel: 'OPEN ORDERS', panel: 'open' },
  { key: 'future_orders', label: 'Future Orders', icon: 'schedule', tabLabel: 'FUTURE ORDERS', panel: 'future' },
  {
    key: 'deliveries', label: 'Deliveries', icon: 'delivery_dining', tabLabel: 'DELIVERIES', panel: 'deliveries',
    visible: store => !!store?.has_order_occasion_delivery
  },
  { key: 'reporting', label: 'Reporting', icon: 'bar_chart', tabLabel: 'REPORTING', panel: 'reporting' },
  {
    key: 'cash_logs', label: 'Cash Logs', icon: 'point_of_sale', tabLabel: 'CASH LOGS', panel: 'cash',
    visible: store => !!store?.feature_flag?.['cash_drawer_management'] || !!store?.feature_flag?.['cash_hot_buttons_enabled']
  },
];

/** Whether the store has opted in. `store` may be a plain object or a Store instance. */
export function uiOrderEnabled(store: any): boolean {
  return !!store?.feature_flag?.[UI_ORDER_FLAG];
}

/** The saved list, or [] when the flag is off or nothing was ever saved. */
export function savedNavOrder(store: any): string[] {
  return uiOrderEnabled(store) ? asKeyList(store?.store_data?.nav_order) : [];
}

export function savedOrdersTabOrder(store: any): string[] {
  return uiOrderEnabled(store) ? asKeyList(store?.store_data?.orders_tab_order) : [];
}

function asKeyList(value: any): string[] {
  return Array.isArray(value) ? value.filter(k => typeof k === 'string') : [];
}

/**
 * The position `key` should render at. Listed keys come first in list order; everything
 * else follows in its built-in order (`fallbackIndex`). With an empty list this is just
 * `fallbackIndex`, i.e. today's order.
 */
export function uiOrderIndex(saved: string[], key: string, fallbackIndex: number): number {
  const i = saved.indexOf(key);
  return i !== -1 ? i : saved.length + fallbackIndex;
}

/**
 * `items` sorted by the saved list (stable: unlisted items keep their relative order).
 * Pinned items (see OrdersTabItem.pinned) stay at the front in their built-in order.
 */
export function applyUiOrder<T extends UiOrderItem>(items: T[], saved: string[], keyOf: (item: T) => string): T[] {
  if (!saved.length) { return items.slice(); }
  const pinned = items.filter(item => item.pinned);
  const movable = items.filter(item => !item.pinned);
  return pinned.concat(movable
    .map((item, index) => ({ item, rank: uiOrderIndex(saved, keyOf(item), index) }))
    .sort((a, b) => a.rank - b.rank)
    .map(x => x.item));
}

/** The items a store can show at all — what the picker lists. Permission is per user, not per store, so it is not applied here. */
export function itemsForStore<T extends UiOrderItem>(items: T[], store: any): T[] {
  const modules: string[] = store?.modules || [];
  return items.filter(item =>
    (!item.module || modules.includes(item.module)) &&
    (!item.visible || item.visible(store)));
}
